import type { ReactNode } from 'react';
import { CardMasonry } from '../reactbits/Masonry';
import { Icon } from '../common/Icon';
import { TimeStamp } from './Text';

/**
 * Masonry — the responsive multi-column waterfall layout used by the
 * Feed and any other "card stream" surface.
 *
 * This is a thin wrapper around the existing `CardMasonry` engine in
 * `components/reactbits/Masonry.tsx` (which handles the hard parts:
 * ResizeObserver-based real heights, even-distribution column-fill
 * algorithm, smooth transitions). The layout namespace re-exports it
 * here so pages can `import { Masonry } from '@/components/layout'`
 * alongside the other primitives instead of reaching into reactbits.
 *
 * Default columns are tuned for the project's content density:
 *
 *     mobile (default):  2
 *     sm  ≥ 640px:       2
 *     md  ≥ 768px:       3
 *     lg  ≥ 1024px:      3
 *     xl  ≥ 1280px:      4
 *
 * Override per-page when the cards are wider/narrower than typical.
 *
 * @example
 * <Masonry>
 *   {posts.map(p => (
 *     <MasonryCard
 *       key={p.id}
 *       image={p.imageUrl}
 *       title={p.title}
 *       body={p.body}
 *       footer={<TimeStamp>{p.timeAgo}</TimeStamp>}
 *       onClick={() => openPost(p)}
 *     />
 *   ))}
 * </Masonry>
 */
export interface MasonryProps {
  children: ReactNode;
  /** Responsive column counts per Tailwind breakpoint. Pass `default`
   * for mobile and override individual breakpoints. */
  columns?: { default: number; sm?: number; md?: number; lg?: number; xl?: number; '2xl'?: number };
  /** Pixel gap between cards horizontally and vertically. */
  gap?: number;
  /** Fade-in stagger animation as new rows mount. */
  animate?: boolean;
  className?: string;
}

export function Masonry({
  children,
  columns = { default: 2, sm: 2, md: 3, lg: 3, xl: 4 },
  gap = 6,
  animate = false,
  className,
}: MasonryProps) {
  return (
    <CardMasonry columns={columns} gap={gap} animate={animate} className={className}>
      {children}
    </CardMasonry>
  );
}

// ─── MasonryCard ──────────────────────────────────────────────────────────

export interface MasonryCardAuthor {
  /** Avatar image URL. Renders at 28×28 (larger than the rest of the
   * footer text so the author reads as a real identity, not a meta
   * field). Falls back to `avatarFallback` if not provided. */
  avatar?: string;
  /** Single-character/short fallback rendered in a soft primary
   * circle when `avatar` is missing. Defaults to the first letter
   * of `name`. */
  avatarFallback?: string;
  /** Display name. Plain text by default; pass a ReactNode for
   * mixed-style names (e.g. `<>Sage <Verified/></>`) or wrap in your
   * own span/gradient for a decorated look. */
  name: ReactNode;
  /** Tailwind class string applied to the name element. Use this
   * for special styling on top users / dying agents / featured
   * creators (`text-primary`, `font-semibold`, gradient text, etc.). */
  nameClassName?: string;
  /** Click handler scoped to the author chip — typically navigates
   * to the agent's profile. Stops propagation so it doesn't trigger
   * the card-level `onClick`. */
  onClick?: () => void;
}

export interface MasonryCardProps {
  /** Optional cover image URL. When present, renders at the top of
   * the card with a fixed aspect frame. */
  image?: string;
  /** Optional video thumbnail. Same slot as `image` but adds a play
   * indicator overlay. */
  videoThumbnail?: string;
  /** Optional small label shown over the cover image (community
   * name, category badge, etc). */
  imageLabel?: ReactNode;
  /** Card title — short, 1-2 lines. */
  title?: ReactNode;
  /** Card body — typically 2-4 lines of preview text, line-clamped. */
  body?: ReactNode;
  /** Author identity — avatar + name. Rendered in the footer row
   * with a larger avatar than the surrounding meta. The author can
   * be tapped independently of the card. */
  author?: MasonryCardAuthor;
  /** Footer right slot — sits opposite the author. Typically holds
   * the timestamp and interaction counts (likes, replies). */
  meta?: ReactNode;
  /** Tap handler for the whole card. */
  onClick?: () => void;
  /** Reduces the card to grayscale + 75% opacity. Use for "dead" or
   * archived items in the feed. */
  muted?: boolean;
}

/**
 * The canonical card shape inside a `<Masonry>`. No border, no card
 * background, no shadow — pure content stacked on the page background
 * with a hairline divider before the footer when needed. Real Feed
 * cards (`DefaultCard`, `TaskCompletionCard`, etc.) can compose more
 * elaborate content but should follow this same shape.
 *
 * @example
 * <MasonryCard
 *   image="https://..."
 *   imageLabel={<span className="text-[10px] text-white/85">AAAI 2026</span>}
 *   title="Scaling Laws for Multi-Agent Coordination"
 *   body="提出了 Agent 协作效率与团队规模之间的幂律关系..."
 *   footer={<>
 *     <span className="text-xs text-gray-500">Sage</span>
 *     <Dot/>
 *     <TimeStamp>12m</TimeStamp>
 *   </>}
 *   onClick={() => navigate(`/post/${p.id}`)}
 * />
 */
export function MasonryCard({
  image,
  videoThumbnail,
  imageLabel,
  title,
  body,
  author,
  meta,
  onClick,
  muted,
}: MasonryCardProps) {
  const cover = image || videoThumbnail;
  // Text-only cards need a subtle wash to separate from the page
  // background — without a cover image they'd otherwise blend straight
  // into the page. Cards with a cover image carry their own visual
  // weight via the image, so they stay transparent.
  const isTextOnly = !cover;
  return (
    <article
      onClick={onClick}
      className={`
        ${onClick ? 'cursor-pointer' : ''}
        ${muted ? 'opacity-75 grayscale' : ''}
        ${isTextOnly ? 'rounded p-3 bg-gray-50 dark:bg-white/[0.03]' : ''}
      `}
    >
      {cover && (
        <div className="relative rounded overflow-hidden bg-gray-100 dark:bg-white/[0.03]">
          <img src={cover} alt="" loading="lazy" className="w-full block" />
          {videoThumbnail && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-10 h-10 rounded-full bg-black/55 text-white flex items-center justify-center">
                <Icon name="play_arrow" size={20} />
              </span>
            </div>
          )}
          {imageLabel && (
            <>
              <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 to-transparent" />
              <div className="absolute bottom-1.5 left-2">{imageLabel}</div>
            </>
          )}
        </div>
      )}
      {(title || body) && (
        <div className={cover ? 'pt-3 px-1' : ''}>
          {title && (
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
              {title}
            </h3>
          )}
          {body && (
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-3 mt-1.5 leading-relaxed">
              {body}
            </p>
          )}
        </div>
      )}
      {(author || meta) && (
        <div className={`pt-3 ${cover ? 'px-1 pb-1' : ''} flex items-center gap-2`}>
          {author && <Author author={author} />}
          {meta && (
            <div className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              {meta}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Author chip ──────────────────────────────────────────────────────────

/**
 * Avatar + name pair shown in the footer of a `MasonryCard`. Avatar is
 * 28×28 — deliberately larger than the surrounding 11px meta text so
 * the author reads as a real identity, not just another meta field.
 *
 * The name accepts a `nameClassName` so callers can apply gradients,
 * primary color, weight changes, italics, etc. without us having to
 * predict every possible decoration up front.
 */
function Author({ author }: { author: MasonryCardAuthor }) {
  const fallbackChar =
    author.avatarFallback ??
    (typeof author.name === 'string' && author.name.length > 0
      ? author.name[0].toUpperCase()
      : '?');

  return (
    <button
      type="button"
      onClick={(e) => {
        if (author.onClick) {
          e.stopPropagation();
          author.onClick();
        }
      }}
      className={`flex items-center gap-2 min-w-0 ${author.onClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} transition-opacity`}
    >
      <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center">
        {author.avatar ? (
          <img
            src={author.avatar}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="text-[11px] font-bold text-primary">{fallbackChar}</span>
        )}
      </div>
      <span
        className={
          author.nameClassName ??
          'text-xs font-medium text-gray-700 dark:text-gray-300 truncate'
        }
      >
        {author.name}
      </span>
    </button>
  );
}

// Re-export TimeStamp here too so callers using `<MasonryCard>` don't
// need a second import for the common timestamp pattern in meta rows.
export { TimeStamp };
